import { JobStatus, Report } from '@/api/generatedApi';
import { FailureDiagnostics } from '@/components/FailureDiagnostics/FailureDiagnostics';
import { useGetFailureDiagnosticsAvailability } from '@/hooks/api/assets/useGetFailureDiagnosticsAvailability';
import { StatusBadge } from '@/components/StatusBadge';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { Box, Paper, Typography, styled } from '@mui/material';
import { REPORT_TITLES } from './consts';

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.default,
}));

const Content = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

// Wider than the generating card: a 36 character trace id has to sit on one line to stay copyable.
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: 520,
}));

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const Diagnostics = styled(Box)({
  alignSelf: 'stretch',
});

export const FailedReport = ({ report }: { report: Report }) => {
  const { data: availability } = useGetFailureDiagnosticsAvailability();
  // Not every tenant runs with log search. Where it is off, the diagnostics panel can only say so,
  // which tells the reader nothing they can act on, so say what they can do instead.
  const canSearchLogs = availability?.available !== false;

  return (
    <Root data-testid={ReportPageObjectModel.FailedReport}>
      <Content>
        <StyledPaper elevation={3}>
          <Typography variant='h5'>{report.name || REPORT_TITLES[report.reportType]}</Typography>
          <StatusBadge status={JobStatus.FAILED} />
          {canSearchLogs ? (
            <>
              <Description variant='body2'>This report did not finish. Here is what the logs say about it.</Description>
              <Diagnostics>
                <FailureDiagnostics assetId={report.id} kind='report' />
              </Diagnostics>
            </>
          ) : (
            <Description variant='body2' data-testid='failed-report-without-logs'>
              This report did not finish. Try running it again, and if it keeps failing contact support with the report
              name and roughly when it ran.
            </Description>
          )}
        </StyledPaper>
      </Content>
    </Root>
  );
};
